/**
 * RequirementsTopbar: page title plus segmented view switch.
 * Reads/writes `view` from the view store; render-only, no data fetching.
 * Visual idiom aligned with Documents3 page header.
 */
import { FileText, LayoutGrid, List, PenLine, Plus, Download } from 'lucide-react'
import { useRequirementsViewStore, type RequirementsView } from '@/stores/requirementsViewStore'

const VIEWS: Array<{ id: RequirementsView; label: string; description: string; icon: typeof FileText }> = [
  { id: 'outline',  label: 'Outline',  description: 'Document-style outline. Click to edit, ↩ for new line, ⇥ to indent', icon: List },
  { id: 'document', label: 'Document', description: 'Read it like a PRD: sections, epics, stories with descriptions', icon: FileText },
  { id: 'draft',    label: 'Draft',    description: 'Capture requirements from raw notes using #, bullets, and + criteria', icon: PenLine },
  { id: 'table',    label: 'Table',    description: 'Spreadsheet view: sort, filter, edit fields in bulk',           icon: LayoutGrid },
]

interface Props {
  count: number
  onAddClick: () => void
  onExportClick?: () => void
}

export function RequirementsTopbar({ count, onAddClick, onExportClick }: Props) {
  const view = useRequirementsViewStore(s => s.view)
  const setView = useRequirementsViewStore(s => s.setView)

  return (
    <header
      className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
      style={{
        borderBottom: '1px solid var(--color-warm-200)',
        background: 'color-mix(in oklab, var(--color-warm-50) 80%, transparent)',
      }}
    >
      <div className="flex items-baseline gap-2 min-w-0">
        <h1
          className="m-0 truncate"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em',
            color: 'var(--color-fg)',
          }}
        >
          Requirements
        </h1>
        <span className="text-xs flex-shrink-0" style={{ color: 'var(--color-fg-muted)' }}>
          · {count} item{count === 1 ? '' : 's'}
        </span>
      </div>
      <div className="flex-1" />

      {/* Documents3-style segmented control */}
      <div
        role="tablist"
        aria-label="View mode"
        className="inline-flex items-center gap-0.5 p-1 rounded-lg"
        style={{
          background: 'var(--color-warm-100)',
          border: '1px solid var(--color-warm-200)',
        }}
      >
        {VIEWS.map(({ id, label, description, icon: Icon }) => {
          const active = view === id
          return (
            <button
              key={id}
              role="tab"
              aria-selected={active}
              onClick={() => setView(id)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-2"
              style={{
                background: active ? 'var(--color-bg-card)' : 'transparent',
                color: active ? 'var(--color-fg)' : 'var(--color-fg-secondary)',
                fontWeight: active ? 600 : 500,
                boxShadow: active ? '0 1px 2px rgba(29,26,21,0.06)' : undefined,
                ['--tw-ring-color' as string]: 'var(--color-accent)',
              }}
              title={description}
              aria-label={`${label} view: ${description}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          )
        })}
      </div>

      {onExportClick && (
        <button
          onClick={onExportClick}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2"
          style={{
            border: '1px solid var(--color-warm-200)',
            color: 'var(--color-fg)',
            background: 'var(--color-bg-card)',
            ['--tw-ring-color' as string]: 'var(--color-accent)',
          }}
          title="Export requirements"
        >
          <Download className="h-4 w-4" /> Export
        </button>
      )}

      <button
        onClick={onAddClick}
        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2"
        style={{
          background: 'var(--color-solution)',
          color: 'var(--color-fg)',
          border: '1px solid var(--color-solution)',
          ['--tw-ring-color' as string]: 'var(--color-accent)',
        }}
        title="Add a new requirement"
      >
        <Plus className="h-4 w-4" /> New
      </button>
    </header>
  )
}
